// Sprint 2026-Jun — Single source of truth for sidebar + route permission
// gating. STRICT model per user directive: "No access means nothing to show."
// Every nav item requires its OWN module `view` grant. The only fallbacks
// kept are ones where the umbrella key GENUINELY governs the backend API
// for that feature (so showing the item is not a leak — the user really
// does have access to the data behind it).
import {
  LayoutDashboard, FolderKanban, Users, Package,
  Warehouse, ShoppingCart, ReceiptText, Wallet, Shield,
  FileBarChart2, HardHat, Briefcase, BookOpen,
  Banknote, ArrowDownToLine, FileText as FileTextIcon, UserSquare2,
  Scale, Calculator, FileSignature, Hash, Building, CalendarRange,
  Library, Ruler, FolderTree, ShieldCheck, Inbox, ClipboardList,
  Home as HomeIcon, BadgeDollarSign, CreditCard, Send, Landmark,
  PackageMinus, PackagePlus, ListChecks, ArrowRightLeft, Database,
  SlidersHorizontal,
  Play,
} from "lucide-react";

export const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
      { to: "/ops-dashboard", label: "Operations Dashboard", icon: HardHat, module: "ops_dashboard" },
      { to: "/project-health", label: "Project Health", icon: FileBarChart2, module: "project_health" },
      { to: "/reports/performance-pnl", label: "Project Monthly Performance P&L", icon: FileBarChart2, module: "performance_pnl" },
      { to: "/debtor-dashboard", label: "Debtor/HR Management Dashboard", icon: LayoutDashboard, module: "debtor_dashboard" },
      { to: "/reports/aging-outstanding", label: "Aging Outstanding (Receivables)", icon: FileBarChart2, module: "aging_outstanding_report" },
    ],
  },
  {
    label: "Masters",
    items: [
      { to: "/projects", label: "Projects", icon: FolderKanban, module: "projects" },
      { to: "/clients", label: "Clients", icon: Users, module: "clients" },
      { to: "/vendors", label: "Vendors", icon: HardHat, module: "vendors" },
      { to: "/members", label: "Members", icon: UserSquare2, module: "members" },
      { to: "/labour-library", label: "Labour Library", icon: HardHat, module: "labour_library" },
      { to: "/category-master", label: "Category Master", icon: FolderTree, module: "project_grn" },
      { to: "/items", label: "Items", icon: Package, module: "items" },
      { to: "/masters/bank-ledger", label: "Bank Ledger", icon: Banknote, module: "bank_ledger" },
    ],
  },
  {
    label: "Inventory & Assets",
    items: [
      { to: "/inventory", label: "Stock", icon: Warehouse, module: "inventory" },
      { to: "/assets", label: "Assets", icon: Briefcase, module: "assets" },
      { to: "/issue-vouchers", label: "Issue Voucher", icon: PackageMinus, module: "issue_voucher" },
      { to: "/return-vouchers", label: "Return Voucher", icon: PackagePlus, module: "return_voucher" },
      { to: "/returnable-register", label: "Returnable Register", icon: ListChecks, module: "returnable_register" },
      { to: "/stock-transfers", label: "Stock Transfer", icon: ArrowRightLeft, module: "stock_transfer" },
      { to: "/material-transfers", label: "Material / Asset Transfer", icon: ArrowRightLeft, module: "material_transfer" },
      { to: "/warehouse-transfers", label: "Warehouse Transfer", icon: ArrowRightLeft, module: "warehouse_transfer" },
      { to: "/warehouse-movements", label: "WH Movement Ledger", icon: ArrowRightLeft, module: "warehouse_movements" },
      { to: "/stock-adjustments", label: "Stock Adjustment", icon: ClipboardList, module: "stock_adjustment" },
      { to: "/asset-lifecycle", label: "Asset/Inventory Lifecycle (Repairs · Scrap · Depreciation)", icon: Scale, module: "depreciation" },
      { to: "/reports/issued-items", label: "Issued Items Report", icon: FileBarChart2, module: "issued_items_report" },
      { to: "/reports/returnables", label: "Returnable Tracking", icon: FileBarChart2, module: "returnable_report" },
      { to: "/reports/issued-statement", label: "Issued Statement (PDF)", icon: FileBarChart2, module: "issued_statement" },
    ],
  },
  {
    label: "Sales Transactions",
    items: [
      { to: "/estimates", label: "Estimates", icon: FileSignature, module: "estimates" },
      { to: "/estimates/approved-queue", label: "Approved Estimate Queue", icon: ListChecks, module: "approved_estimate_queue" },
      { to: "/jms", label: "JMS", icon: ClipboardList, module: "jms" },
      { to: "/sales-invoices", label: "Sales Invoices", icon: ReceiptText, module: "sales_invoices" },
      { to: "/sales-credit-notes", label: "Sales Credit Note", icon: ReceiptText, module: "sales_credit_note" },
      { to: "/sales-debit-notes", label: "Sales Debit Note", icon: ReceiptText, module: "sales_debit_note" },
      { to: "/reports/estimate-ageing", label: "Estimate Ageing Summary", icon: FileBarChart2, module: "estimate_ageing_report" },
      { to: "/oem-complaints", label: "OEM Complaint", icon: ClipboardList, module: "oem_complaints" },
    ],
  },
  {
    label: "Receivables",
    items: [
      { to: "/invoice-queue", label: "Invoice Queue", icon: ListChecks, module: "invoice_queue" },
      { to: "/bulk-receipts", label: "Bulk Receipts", icon: ArrowDownToLine, module: "bulk_receipts" },
      { to: "/receipt-voucher-register", label: "Receipt Voucher Register", icon: ClipboardList, module: "receipt_voucher_register" },
      { to: "/reports/tds-recovery", label: "TDS Recovery Report", icon: FileBarChart2, module: "tds_recovery_report" },
      { to: "/receivable-retentions", label: "Retentions (Receivable)", icon: Landmark, module: "receivable_retentions" },
      { to: "/receivables/due-dates", label: "Due Date Editor", icon: CalendarRange, module: "due_date_editor" },
      { to: "/reports/receivables", label: "Receivables Reports", icon: FileBarChart2, module: "receivables_reports" },
    ],
  },
  {
    label: "Purchase",
    items: [
      { to: "/purchase-orders", label: "Purchase Order", icon: ClipboardList, module: "purchase_order" },
      { to: "/purchase-invoices", label: "Purchase Invoice", icon: ShoppingCart, module: "purchase_invoices" },
      { to: "/purchase/store-rent", label: "Store Rent Payment", icon: HomeIcon, module: "payments" },
      { to: "/purchase/salary", label: "Salary Payment", icon: BadgeDollarSign, module: "payments" },
      { to: "/purchase/general", label: "General Payment", icon: CreditCard, module: "payments" },
      { to: "/purchase/bill-payment", label: "Bill Payment", icon: Wallet, module: "payments" },
      { to: "/purchase/post-payment", label: "Post Payment", icon: Send, module: "payments" },
      { to: "/bank-reconciliation", label: "Bank Reconciliation", icon: Landmark, module: "payments" },
    ],
  },
  {
    label: "Approvals",
    items: [
      { to: "/approvals", label: "Approvals", icon: Inbox, module: "approvals" },
      { to: "/one",       label: "Official Notes", icon: ClipboardList, module: "one" },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/ledgers", label: "Ledgers", icon: BookOpen, module: "ledgers" },
      { to: "/trial-balance", label: "Trial Balance", icon: Scale, module: "trial_balance" },
      { to: "/general-ledger", label: "General Ledger", icon: Calculator, module: "general_ledger" },
      { to: "/journal-vouchers", label: "Journal Voucher", icon: Calculator, module: "journal_voucher_eb" },
      { to: "/jv-ledgers", label: "Ledger Master", icon: Calculator, module: "jv_ledger_master" },
      { to: "/reports/gl-audit", label: "GL Integrity Audit", icon: Scale, module: "gl_audit" },
      { to: "/reports/financial-integrity", label: "Financial Integrity", icon: ShieldCheck, module: "financial_integrity" },
      { to: "/reports/balance-sheet", label: "Balance Sheet (Sch III)", icon: Landmark, module: "balance_sheet" },
      { to: "/reports/profit-loss", label: "Profit & Loss (Sch III)", icon: FileBarChart2, module: "profit_loss" },
      { to: "/opening-balance", label: "Opening Balance Wizard", icon: Scale, module: "opening_balance" },
    ],
  },
  {
    label: "Documents & Reports",
    items: [
      { to: "/reports", label: "Reports", icon: FileBarChart2, module: "reports" },
      { to: "/reports/project-pnl", label: "Project P&L", icon: FileBarChart2, module: "project_pnl" },
      { to: "/reports/branch-pnl", label: "Branch P&L", icon: FileBarChart2, module: "branch_pnl" },
      { to: "/reports/vertical-pnl", label: "Vertical P&L", icon: FileBarChart2, module: "vertical_pnl" },
      { to: "/reports/warehouse-inventory", label: "Warehouse Inventory", icon: FileBarChart2, module: "warehouse_inventory_report" },
      { to: "/reports/cost-centre-expense", label: "Cost Centre Expense", icon: FileBarChart2, module: "cost_centre_expense" },
      { to: "/reports/company-pnl", label: "Company P&L", icon: FileBarChart2, module: "company_pnl" },
      { to: "/reports/scan-activity", label: "Scan Activity Report", icon: FileBarChart2, module: "scan_activity_report" },
      { to: "/reports/identity-audit", label: "Identity Audit Sweep", icon: ShieldCheck, module: "identity_audit" },
      { to: "/gst", label: "GST Centre", icon: FileBarChart2, module: "gst_centre" },
    ],
  },
  {
    label: "Compliance",
    items: [
      { to: "/hr-compliance", label: "HR Compliance", icon: ShieldCheck, module: "hr_compliance" },
    ],
  },
  {
    label: "Overhead Allocation",
    items: [
      { to: "/overhead-allocation/rules",     label: "Allocation Rules",  icon: ListChecks,   module: "overhead_allocation" },
      { to: "/overhead-allocation/snapshots", label: "Basis Snapshots",   icon: ClipboardList, module: "overhead_allocation" },
      { to: "/overhead-allocation/runs",      label: "Run Workbench",     icon: Play,         module: "overhead_allocation" },
      { to: "/overhead-allocation/register",  label: "Allocation Register", icon: ClipboardList, module: "overhead_allocation" },
    ],
  },
  {
    label: "Administration",
    items: [
      { to: "/control-centre", label: "Financial Control Centre", icon: ShieldCheck, module: "financial_control_centre" },
      { to: "/settings/company", label: "Company Setup", icon: Building, module: "company_settings", adminOnly: true },
      { to: "/settings/gst-api-config", label: "GST E-Invoice API", icon: ShieldCheck, module: "gst_config", adminOnly: true },
      { to: "/settings/financial-year", label: "Financial Year", icon: CalendarRange, module: "financial_year", adminOnly: true },
      { to: "/library/item-groups", label: "Item Groups", icon: FolderTree, module: "item_groups", adminOnly: true },
      { to: "/library/project-groups", label: "Project Groups", icon: FolderKanban, module: "project_groups", adminOnly: true },
      { to: "/library/hsn-sac", label: "HSN / SAC Library", icon: Hash, module: "hsn_sac", adminOnly: true },
      { to: "/library/uom", label: "UOM Library", icon: Ruler, module: "uom", adminOnly: true },
      { to: "/management-accounting/cost-centres", label: "Cost Centres", icon: Building, module: "management_accounting", adminOnly: true },
      { to: "/users", label: "Users & Roles", icon: Shield, module: "users", adminOnly: true },
      { to: "/settings/project-ops-control", label: "Project Operations Control", icon: SlidersHorizontal, module: "project_ops_admin" },
      { to: "/settings/approval-matrix", label: "Approval Matrix", icon: ShieldCheck, module: "approval_matrix", adminOnly: true },
      { to: "/numbering", label: "Numbering Series", icon: Library, module: "numbering", adminOnly: true },
      { to: "/settings/email", label: "Email Configuration", icon: Send, module: "email_config", adminOnly: true },
      { to: "/settings/data-tools", label: "Data Tools", icon: Database, module: "data_tools", adminOnly: true },
      { to: "/settings/gl-cleanup", label: "GL Cleanup (Orphans)", icon: Database, module: "gl_cleanup", adminOnly: true },
      { to: "/settings/pi-stock-audit", label: "PI Stock Audit", icon: Package, module: "pi_stock_audit", adminOnly: true },
    ],
  },
];

// Modules whose sidebar/route visibility may legitimately fall back to a
// broader key — ONLY because the backend API for the feature is actually
// gated by (or reachable through) that broader key. Everything NOT in
// this map is strictly own-key.
const TRUE_UMBRELLAS = {
  sales_credit_note:   ["sales_invoices"],
  sales_debit_note:    ["sales_invoices"],
  payment_receipt:     ["payments"],
  purchase_order:      ["purchase_invoices"],
  issue_voucher:       ["inventory"],
  return_voucher:      ["inventory"],
  returnable_register: ["inventory"],
  stock_transfer:      ["inventory"],
  material_transfer:   ["inventory"],
  warehouse_transfer:  ["inventory"],
  warehouse_movements: ["inventory"],
  stock_adjustment:    ["inventory"],
  depreciation:        ["assets"],
  overhead_allocation: ["management_accounting"],
  gst_centre:          ["gst"],
  // Phase F — the register API genuinely accepts either key.
  receipt_voucher_register: ["bulk_receipts"],
  // Jun-2026 — the Journal Voucher sidebar item is keyed on the EB module;
  // a user granted only FC / OE / OI must still reach the JV screen (each
  // category tab is separately gated inside the page).
  journal_voucher_eb: ["journal_voucher_fc", "journal_voucher_oe", "journal_voucher_oi"],
};

export function canSeeModule(item, hasPerm, user) {
  const module = typeof item === "string" ? item : item?.module;
  if (!module) return false;
  if (user?.role === "admin") return true;
  // Dashboard is the landing page — always reachable; its figures are
  // separately gated by `dashboard.view` inside the page itself.
  if (module === "dashboard") return true;
  if (hasPerm(module, "view")) return true;
  for (const fb of TRUE_UMBRELLAS[module] || []) {
    if (hasPerm(fb, "view")) return true;
  }
  return false;
}

// path → nav item lookup (exact match) for the deep-link route guard.
const PATH_ITEM = {};
NAV_SECTIONS.forEach((s) => s.items.forEach((i) => { PATH_ITEM[i.to] = i; }));

export function canSeePath(path, hasPerm, user) {
  const item = PATH_ITEM[path];
  if (!item) return true; // dynamic / form / detail tabs — spawned from an allowed page
  return canSeeModule(item, hasPerm, user);
}
